import { useState } from 'react'
import useSWR from 'swr'
import { fetchWithToken } from './http'

export interface PlanMetadata {
  id: string
  revision: number
  userId: string
  employeeFirstName: string
  employeeLastName: string
  memberOrganizationId?: string
  memberOrganizationName?: string
  organizationId: string
  rootOrganizationId: string
  rootPeriodId: string
  periodId: string
  position: string
  templateId?: string
  templateLabel?: string
  periodProration: number
  compensationModel: string
  baseSalary: number
  variablePercent: number
  variableCap: number
  effectiveDate: string
  endDate?: string
  finalizedAt?: string
  approvalBatchId?: string
  createdAt: string
  isRolledOver: boolean
  isHidden?: boolean
}

// Valid values for finalizedFilter
export type FinalizedFilter = 'open' | 'all' | 'beforePeriodEnd' | 'atPeriodEnd'

export function usePlansMetadata(
  token: string,
  rootOrganizationId: string | null,
  rootPeriodId: string | null,
  periodId: string | null,
  organizationIds: string[],
  templateId: string,
  searchTerm: string,
  sort: string,
  order: string,
  finalizedFilter: FinalizedFilter = 'all',
  includeRolledOver: boolean = false,
  includeHidden: boolean = true,
  includeVisible: boolean = true,
  offset: number = 0,
  limit: number = 50,
  shouldFetch: boolean = true
) {
  const [isLoading, setIsLoading] = useState(false)
  const shouldFetchData =
    token != null &&
    token.trim() !== '' &&
    rootOrganizationId != null &&
    rootPeriodId != null &&
    periodId != null &&
    shouldFetch

  const params = new Array<[string, string]>()
  if (rootOrganizationId && rootPeriodId) {
    params.push(['rootOrganizationId', rootOrganizationId])
    params.push(['rootPeriodId', rootPeriodId])
  }
  if (organizationIds.length > 0) {
    params.push(['organizationIds', organizationIds.join(',')])
  }
  if (templateId) {
    params.push(['template', templateId])
  }
  if (searchTerm) {
    params.push(['searchTerm', searchTerm])
  }
  if (sort) {
    params.push(['sort', sort])
  }
  if (order) {
    params.push(['order', order])
  }
  params.push(['finalizedFilter', finalizedFilter])
  params.push(['includeRolledOver', includeRolledOver ? 'true' : 'false'])
  params.push(['includeHidden', includeHidden ? 'true' : 'false'])
  params.push(['includeVisible', includeVisible ? 'true' : 'false'])

  // Convert offset/limit to page/perPage for standardized pagination
  const page = Math.floor(offset / limit) + 1 // Convert 0-based offset to 1-based page
  params.push(['page', page.toString()])
  params.push(['perPage', limit.toString()])

  // Debug logging to see what params are being sent
  const debugUrl = shouldFetchData
    ? `compensation/plans/metadata/${periodId}?${params.map(([k, v]) => `${k}=${v}`).join('&')}`
    : null
  if (debugUrl) {
    console.log('DEBUG: Frontend requesting URL:', debugUrl)
    console.log('DEBUG: includeRolledOver param being sent:', params.find(([k, v]) => k === 'includeRolledOver')?.[1])
    console.log('DEBUG: SWR key will be:', [`compensation/plans/metadata/${periodId}`, token, params])
  }

  const { data, error, mutate } = useSWR(
    shouldFetchData ? [`compensation/plans/metadata/${periodId}`, token, params] : null,
    ([url, token, params]) => fetchWithToken(url, token, params.length > 0 ? params : null),
    {
      suspense: false,
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnMount: true,
      revalidateOnReconnect: true,
      refreshInterval: 0,
      dedupingInterval: 0,
      focusThrottleInterval: 0,
    }
  )

  const handleRefetch = () => {
    setIsLoading(true)
    mutate().finally(() => {
      setIsLoading(false)
    })
  }

  // Optimistic update helper to remove plans from the cache
  const optimisticRemovePlans = (planIds: string[]) => {
    const planIdSet = new Set(planIds)
    mutate(
      (currentData: { plans: PlanMetadata[]; pagination?: any } | PlanMetadata[] | undefined) => {
        if (!currentData) return currentData
        if (Array.isArray(currentData)) {
          return currentData.filter((plan) => !planIdSet.has(plan.id))
        }
        return {
          ...currentData,
          plans: currentData.plans.filter((plan) => !planIdSet.has(plan.id)),
          pagination: currentData.pagination
            ? {
                ...currentData.pagination,
                totalItems: Math.max(0, (currentData.pagination.totalItems || 0) - planIds.length),
              }
            : undefined,
        }
      },
      { revalidate: false }
    )
  }

  // Optimistic update helper to update plans in the cache
  const optimisticUpdatePlans = (updates: Array<{ id: string; changes: Partial<PlanMetadata> }>) => {
    const updateMap = new Map(updates.map((u) => [u.id, u.changes]))
    mutate(
      (currentData: { plans: PlanMetadata[]; pagination?: any } | PlanMetadata[] | undefined) => {
        if (!currentData) return currentData
        if (Array.isArray(currentData)) {
          return currentData.map((plan) => {
            const changes = updateMap.get(plan.id)
            return changes ? { ...plan, ...changes } : plan
          })
        }
        return {
          ...currentData,
          plans: currentData.plans.map((plan) => {
            const changes = updateMap.get(plan.id)
            return changes ? { ...plan, ...changes } : plan
          }),
        }
      },
      { revalidate: false }
    )
  }

  const responseData = data as { plans: PlanMetadata[]; pagination?: any } | PlanMetadata[]

  // Handle both new paginated format and legacy array format for backward compatibility
  const plans = Array.isArray(responseData) ? responseData : responseData?.plans || []
  const pagination = Array.isArray(responseData) ? null : responseData?.pagination

  return {
    plans,
    pagination,
    isLoadingPlansMetadata: isLoading,
    isError: error,
    handleRefetch,
    optimisticRemovePlans,
    optimisticUpdatePlans,
  }
}
